import type { FC } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { bindPopover, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';

import { ArrowDownSmallIcon, CrossSmallIcon } from '@setup/svg-icons/svg-icons';
import { useAppDispatch } from '@shared/hooks/redux';
import { InputError, InputLabel } from '@shared/ui-kit';
import { capitalizeFirstLetter } from '@shared/utils/capitalizeFirstLetter';
import { getRoles } from '@store/network/networkThunk';

import {
  SelectRoleContainer,
  SelectRolePopoverContainer,
  SelectRolePopoverContentContainer,
  SelectRolePopoverContentItem,
  SelectRolePopoverLeftColumnContainer,
  SelectRolePopoverRightColumnContainer,
  SelectRolePopoverSkeletonContainer,
} from './SelectRole.style';

type Props = {
  label?: string;
  onChange: (value: string) => void;
  value: string;
  isError?: boolean;
  errorMessage?: string;
  placeholder: string;
  isCreatable?: boolean;
  isTextVariantInput?: boolean;
};

export const SelectRole: FC<Props> = ({
  label,
  onChange,
  value,
  isError,
  errorMessage,
  placeholder,
  isCreatable = false,
  isTextVariantInput = false,
}) => {
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'emojiPopover',
  });

  const [groupRoleLoading, setGroupRoleLoading] = useState(false);
  const [groupRole, setGroupRole] = useState<string[]>([]);
  const theme = useTheme();
  // TODO eny
  const [roles, setRoles] = useState<{ [key: string]: string[] } | any | {}>({});
  const [itemRole, setItemRole] = useState<string[]>([]);
  const [selectGroupRole, setSelectGroupRole] = useState<string>();
  const [selectItemRole, setSelectItemRole] = useState<string>('friend');
  const ref = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();

  const handleSelectItemRole = (selectValue: string) => {
    onChange(selectValue);
    setSelectItemRole(value);
    popupState.close();
  };

  const selectGroup = (data: { [key: string]: string[] } | any | {}) => {
    setGroupRole(Object.keys(data));
    setSelectGroupRole(Object.keys(data)[0]);
    setItemRole(data.friend);
  };

  const handleSelectGroup = useCallback(
    (selectValue: string) => {
      setSelectGroupRole(selectValue);
      setItemRole(roles[selectValue]);
    },
    [setSelectGroupRole, roles],
  );

  useEffect(() => {
    if (popupState?.isOpen) {
      dispatch(getRoles(setGroupRoleLoading, setRoles, selectGroup));
    } else {
      setGroupRole([]);
      setItemRole([]);
      setSelectItemRole('');
      setSelectGroupRole('');
    }
    return () => {
      setGroupRole([]);
      setItemRole([]);
    };
  }, [dispatch, popupState, setSelectGroupRole, setSelectItemRole]);

  const skeletonArray = Array(3).fill('');
  const skeletonArray2 = Array(5).fill('');
  const themed = useTheme();

  const isSmallDisplay = useMediaQuery(themed.breakpoints.down('sm'));
  return (
    <Box>
      <Box>
        <InputLabel label={label} />
        <SelectRoleContainer
          ref={ref}
          {...bindTrigger(popupState)}
          isError={isError}
          isHasValue={!!value}
          isOpened={popupState?.isOpen}
          isTextVariantInput={isTextVariantInput}
        >
          {value ? (
            <Typography variant="s3" noWrap>
              {value}
            </Typography>
          ) : (
            <Typography variant="s3" sx={{ color: theme.palette.case.neutral.n500 }}>
              {placeholder}
            </Typography>
          )}

          <Box
            sx={{
              width: '16px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              svg: {
                width: 16,
                height: 16,
              },
            }}
          >
            {value?.length > 0 && isCreatable ? (
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  onChange('');
                }}
                sx={{
                  position: 'relative',
                  zIndex: '100',
                  width: '20px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer !important',

                  svg: {
                    path: {
                      fill: theme.palette.case.neutral.n500,
                    },
                  },

                  '&:hover': {
                    opacity: '0.7',
                  },
                }}
              >
                <CrossSmallIcon />
              </Box>
            ) : (
              <Box
                sx={{
                  svg: {
                    width: '24px',
                    height: '24px',
                    transform: popupState.isOpen ? 'rotate(180deg)' : '',
                    path: {
                      fill: theme.palette.case.neutral.n500,
                    },
                  },
                }}
              >
                <ArrowDownSmallIcon />
              </Box>
            )}
          </Box>
        </SelectRoleContainer>
        <InputError isShow={isError} message={errorMessage} />
      </Box>

      <SelectRolePopoverContainer
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        {...bindPopover(popupState)}
      >
        <Box
          sx={{
            height: '227px',
            overflow: 'hidden',
            width: isSmallDisplay ? '250px' : '300px',
          }}
        >
          <SelectRolePopoverContentContainer>
            <SelectRolePopoverLeftColumnContainer>
              {groupRoleLoading
                ? skeletonArray.map((_, index) => (
                    <SelectRolePopoverSkeletonContainer key={index}>
                      <Skeleton variant="rectangular" height={29} width={90} sx={{ borderRadius: '5px' }} />
                    </SelectRolePopoverSkeletonContainer>
                  ))
                : groupRole?.map((item) => (
                    <SelectRolePopoverContentItem
                      isActive={selectGroupRole === item}
                      key={item}
                      onClick={() => handleSelectGroup(item)}
                    >
                      <Typography sx={{ p: '5px 5px 5px 5px' }} variant="small">
                        {capitalizeFirstLetter(item)}
                      </Typography>
                    </SelectRolePopoverContentItem>
                  ))}
            </SelectRolePopoverLeftColumnContainer>
            <SelectRolePopoverRightColumnContainer>
              {groupRoleLoading
                ? skeletonArray2.map((_, index) => (
                    <SelectRolePopoverSkeletonContainer key={index}>
                      <Skeleton variant="rectangular" height={29} width="100%" sx={{ borderRadius: '5px' }} />
                    </SelectRolePopoverSkeletonContainer>
                  ))
                : !!itemRole.length &&
                  itemRole?.map((item) => (
                    <SelectRolePopoverContentItem
                      isActive={selectItemRole === item}
                      key={item}
                      onClick={() => handleSelectItemRole(item)}
                    >
                      <Typography sx={{ p: '5px 0 5px 5px', wordBreak: 'break-all' }} variant="small">
                        {item}
                      </Typography>
                    </SelectRolePopoverContentItem>
                  ))}
            </SelectRolePopoverRightColumnContainer>
          </SelectRolePopoverContentContainer>
        </Box>
      </SelectRolePopoverContainer>
    </Box>
  );
};
